import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  DatabaseBackup,
  Hash,
  ListChecks,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { Panel, num } from "@/components/admin/analytics/AnalyticsKit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  cleanupDemoData,
  createSystemBackup,
  getSystemOverview,
} from "@/lib/admin/system/system.functions";
import { NUMBER_SERIES, PROTECTED_DATA } from "@/lib/admin/system/system.types";

export const Route = createFileRoute("/_admin/admin/production-cleanup")({
  head: () => ({
    meta: [
      { title: "Production Cleanup — Admin" },
      {
        name: "description",
        content:
          "Permanently remove demonstration records after client approval, with a mandatory backup and confirmation.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ProductionCleanupPage,
});

type Summary = { total: number; removed: Record<string, number> };

function ProductionCleanupPage() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getSystemOverview);
  const backup = useServerFn(createSystemBackup);
  const cleanup = useServerFn(cleanupDemoData);

  const [step, setStep] = useState(1);
  const [backupDone, setBackupDone] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [series, setSeries] = useState<string[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "system-overview"],
    queryFn: () => fetchOverview(),
  });

  const superAdmin = data?.settings.is_super_admin === true;
  const entities = useMemo(
    () => (data?.entities ?? []).filter((e) => e.demo_count > 0 || e.real_count > 0),
    [data],
  );
  const selectedCount = useMemo(
    () =>
      entities
        .filter((e) => selected.includes(e.key))
        .reduce((acc, e) => acc + e.demo_count, 0),
    [entities, selected],
  );

  const backupMutation = useMutation({
    mutationFn: () =>
      backup({
        data: {
          label: `Pre-cleanup backup ${new Date().toLocaleString("en-IN")}`,
          kind: "pre_cleanup",
          comments: "Created automatically by the Production Cleanup wizard",
        },
      }),
    onSuccess: () => {
      setBackupDone(true);
      setStep(2);
      toast.success("Backup created.");
      void qc.invalidateQueries({ queryKey: ["admin", "system-backups"] });
    },
    onError: () => toast.error("Backup failed — cleanup is blocked until a backup exists."),
  });

  const cleanupMutation = useMutation({
    mutationFn: () => cleanup({ data: { entities: selected, resetSeries: series } }),
    onSuccess: (res) => {
      setSummary(res);
      setStep(4);
      void qc.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: () => toast.error("Cleanup could not be completed."),
  });

  const toggle = (list: string[], set: (v: string[]) => void, key: string) =>
    set(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const steps = ["Create Backup", "Confirmation", "Select Data", "Summary"];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Production Cleanup"
        description="Permanently remove demonstration records once the client has approved go-live. A backup is always taken first."
      />

      <Panel title="Workflow" subtitle="Backup → Confirmation → Cleanup → Summary">
        <ol className="flex flex-wrap gap-2">
          {steps.map((s, i) => {
            const n = i + 1;
            const state = step > n ? "done" : step === n ? "active" : "todo";
            return (
              <li
                key={s}
                className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${
                  state === "active"
                    ? "border-primary bg-muted font-medium text-foreground"
                    : state === "done"
                      ? "text-muted-foreground"
                      : "text-muted-foreground opacity-70"
                }`}
              >
                {state === "done" ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <span className="tabular-nums">{n}</span>
                )}
                {s}
              </li>
            );
          })}
        </ol>
      </Panel>

      {step === 1 ? (
        <AdminSection
          number={1}
          icon={DatabaseBackup}
          title="Create Backup"
          description="A configuration and row-count snapshot is stored in Backup & Restore before anything is removed."
        >
          <Button
            disabled={!superAdmin || backupMutation.isPending}
            onClick={() => backupMutation.mutate()}
            className="gap-2"
          >
            <DatabaseBackup className="h-4 w-4" aria-hidden />
            {backupMutation.isPending ? "Creating backup…" : "Create Backup & Continue"}
          </Button>
          {!superAdmin ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Only a Super Admin can run Production Cleanup.
            </p>
          ) : null}
        </AdminSection>
      ) : null}

      {step === 2 ? (
        <AdminSection
          number={2}
          icon={AlertTriangle}
          title="Confirmation"
          description="Demo records are deleted permanently. Type CLEANUP to continue."
        >
          <div className="max-w-sm space-y-3">
            <Label htmlFor="confirm">Confirmation</Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CLEANUP"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                <ArrowLeft className="h-4 w-4" aria-hidden /> Back
              </Button>
              <Button
                disabled={confirmText.trim().toUpperCase() !== "CLEANUP" || !backupDone}
                onClick={() => setStep(3)}
                className="gap-2"
              >
                Continue <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        </AdminSection>
      ) : null}

      {step === 3 ? (
        <>
          <AdminSection
            number={3}
            icon={ListChecks}
            title="Select Data to Remove"
            description="Only records tagged as demo data are removed. Real client records are never touched."
            actions={
              <Badge variant="secondary">{num(selectedCount)} demo records selected</Badge>
            }
          >
            <div className="grid gap-x-6 gap-y-1 md:grid-cols-2 xl:grid-cols-3">
              {entities.map((e) => (
                <label
                  key={e.key}
                  className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0"
                >
                  <span className="flex items-center gap-2">
                    <Checkbox
                      checked={selected.includes(e.key)}
                      onCheckedChange={() => toggle(selected, setSelected, e.key)}
                    />
                    <span>{e.label.replace(/^Demo /, "")}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{num(e.demo_count)}</span>
                </label>
              ))}
            </div>
            {isLoading ? (
              <p className="mt-3 text-xs text-muted-foreground">Loading datasets…</p>
            ) : null}
          </AdminSection>

          <AdminSection
            number={4}
            icon={Hash}
            title="Reset Number Series (optional)"
            description="Start the client's numbering from one after cleanup."
            defaultOpen={false}
          >
            <div className="grid gap-x-6 gap-y-1 md:grid-cols-2 xl:grid-cols-3">
              {NUMBER_SERIES.map((s) => (
                <label key={s.key} className="flex items-center gap-2 border-b py-2 text-sm last:border-b-0">
                  <Checkbox
                    checked={series.includes(s.key)}
                    onCheckedChange={() => toggle(series, setSeries, s.key)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </AdminSection>

          <AdminSection
            number={5}
            icon={ShieldCheck}
            title="Never Deleted"
            description="These are always preserved, whatever you select above."
            defaultOpen={false}
          >
            <div className="flex flex-wrap gap-2">
              {PROTECTED_DATA.map((p) => (
                <Badge key={p} variant="secondary" className="font-normal">
                  {p}
                </Badge>
              ))}
            </div>
          </AdminSection>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
              <ArrowLeft className="h-4 w-4" aria-hidden /> Back
            </Button>
            <Button
              variant="destructive"
              disabled={selected.length === 0 || cleanupMutation.isPending}
              onClick={() => cleanupMutation.mutate()}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              {cleanupMutation.isPending ? "Cleaning up…" : "Run Cleanup"}
            </Button>
          </div>
        </>
      ) : null}

      {step === 4 && summary ? (
        <AdminSection
          number={6}
          icon={CheckCircle2}
          title="Cleanup Summary"
          description={`${num(summary.total)} demonstration records were permanently removed.`}
        >
          <ul className="space-y-1.5 text-sm">
            {Object.entries(summary.removed).map(([table, count]) => (
              <li key={table} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="font-medium">{table.replace(/_/g, " ")}</span>
                <span className="text-muted-foreground">— {num(count)} removed</span>
              </li>
            ))}
            <li className="flex items-center gap-2 pt-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="font-semibold">Production Ready</span>
            </li>
          </ul>
          <Button variant="outline" className="mt-4" onClick={() => { setStep(1); setSummary(null); setSelected([]); setConfirmText(""); setBackupDone(false); }}>
            Start again
          </Button>
        </AdminSection>
      ) : null}
    </div>
  );
}
